/* sk-live-event-admin.js — Mobile-first admin controller.
 *
 * The admin is the SAME bracket tree as the viewer (renderMatchCell shared
 * via window.SkLiveEvent), with each cell tap-to-editable. Empty bracket
 * shows a size picker (4/8/16/32 teams). On save the whole bracket JSONB
 * is sent in one upsert; if auto_advance is on, winners flow into parent
 * slots automatically. Only 3 tabs: Bracket / Announce / Header.
 *
 * Multi-division: a tournament can run several independent brackets
 * (Gold / Silver / +more) for upper- vs lower-level athletes. They are
 * stored as { brackets: [ {id,label,size,matches,...}, ... ] } in the same
 * opaque `bracket` JSONB column. A division switcher selects the ACTIVE
 * division; every single-bracket operation below (render, resize, edit,
 * auto-advance) runs against that active division via getActiveBracket(),
 * and saveBracket() reconstructs + persists the whole brackets array.
 * Legacy single-bracket events are read through normalizeBrackets() and so
 * keep working untouched.
 */
(function () {
  'use strict';

  var cfg = window.skLiveEventAdminConfig || {};
  var PROXY = cfg.proxyPath || '/apps/sk-services/live-events';

  function $(root, sel) { return (root || document).querySelector(sel); }
  function $$(root, sel) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }

  function escapeHtml(value) {
    if (value == null) return '';
    return String(value)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function toast(root, message, kind) {
    var el = $(root, '[data-sk-le-admin-toast]');
    if (!el) return;
    el.textContent = message;
    el.setAttribute('data-kind', kind || 'ok');
    el.removeAttribute('hidden');
    clearTimeout(el.__skToastTimer);
    el.__skToastTimer = setTimeout(function () { el.setAttribute('hidden', ''); }, 3500);
  }

  function jsonPost(url, body) {
    return fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body || {})
    }).then(parseJson);
  }
  function jsonGet(url) {
    return fetch(url, { credentials: 'same-origin', headers: { Accept: 'application/json' } }).then(parseJson);
  }
  function parseJson(r) {
    return r.text().then(function (text) {
      var data = null;
      try { data = text ? JSON.parse(text) : null; } catch (_e) { /* noop */ }
      if (!r.ok) {
        var err = new Error('HTTP ' + r.status);
        err.detail = data;
        throw err;
      }
      return data;
    });
  }

  // ---------------------------------------------------------------------------
  // State
  // ---------------------------------------------------------------------------

  function getState(root) {
    if (!root.__skState) root.__skState = { event: null, matches: [], announcements: [], editingPos: null, activeBracketId: null };
    return root.__skState;
  }

  // ---------------------------------------------------------------------------
  // Divisions (Gold / Silver / +more)
  // ---------------------------------------------------------------------------

  var DIVISION_LABELS = ['Gold Bracket', 'Silver Bracket', 'Bronze Bracket'];
  var DEFAULT_DIV_IDS = ['gold', 'silver', 'bronze'];
  function defaultLabelFor(idx) { return DIVISION_LABELS[idx] || ('Division ' + (idx + 1)); }
  function defaultIdFor(idx) { return DEFAULT_DIV_IDS[idx] || ('division-' + (idx + 1)); }
  function genDivisionId() {
    return 'div-' + Date.now().toString(36) + '-' + Math.floor(Math.random() * 1e4).toString(36);
  }
  function divColor(idx) {
    return (window.SkLiveEvent && window.SkLiveEvent.divisionColor)
      ? window.SkLiveEvent.divisionColor(idx)
      : (['#D4AF37', '#A8A9AD', '#CD7F32'][idx] || '#D4AF37');
  }

  /* The working division array lives on state.event.bracket = { brackets: [...] }.
   * divisionList reads it; ensureDivisions normalizes whatever shape came back
   * from the server (legacy single bracket OR new brackets array) into that
   * canonical form, giving each division a stable id and validating the active id. */
  function divisionList(state) {
    var b = state.event && state.event.bracket;
    return (b && Array.isArray(b.brackets)) ? b.brackets : [];
  }

  function ensureDivisions(state) {
    if (!state.event) return [];
    var src = state.event.bracket;
    var divs = (window.SkLiveEvent && window.SkLiveEvent.normalizeBrackets)
      ? window.SkLiveEvent.normalizeBrackets(src)
      : (src && Array.isArray(src.brackets) ? src.brackets : []);
    divs = divs.map(function (d, i) {
      return Object.assign({}, d, { id: d.id || defaultIdFor(i), label: d.label || '' });
    });
    state.event.bracket = { brackets: divs };
    if (!divs.length) {
      state.activeBracketId = null;
    } else if (!divs.some(function (d) { return d.id === state.activeBracketId; })) {
      state.activeBracketId = divs[0].id;
    }
    return divs;
  }

  function getActiveBracket(state) {
    var divs = divisionList(state);
    if (!divs.length) return null;
    for (var i = 0; i < divs.length; i++) {
      if (divs[i].id === state.activeBracketId) return divs[i];
    }
    return divs[0];
  }

  /* POST the current brackets array as-is (optimistic UI already applied by
   * the caller) and reload. Used by add/rename/remove division. */
  function persistDivisions(root) {
    var state = getState(root);
    if (!state.event || !state.event.slug) return;
    renderBracketPanel(root);
    jsonPost(PROXY + '/event/upsert', { slug: state.event.slug, bracket: state.event.bracket })
      .then(function () { toast(root, 'Saved.', 'ok'); return loadSnapshot(root, state.event.slug); })
      .catch(function (err) { toast(root, 'Save failed: ' + (err.detail && err.detail.error || err.message), 'err'); });
  }

  function addDivision(root) {
    var state = getState(root);
    if (!state.event || !state.event.slug) { toast(root, 'Save event details first.', 'err'); return; }
    var divs = divisionList(state).slice();
    // If the first/only division is unlabeled, name it Gold so the two are
    // distinguishable on the storefront once a second division exists.
    if (divs.length === 1 && !divs[0].label) {
      divs[0] = Object.assign({}, divs[0], { label: defaultLabelFor(0) });
    }
    var newDiv = {
      id: genDivisionId(),
      label: defaultLabelFor(divs.length),
      size: 0, team_count: 0, auto_advance: true, matches: [], _meta: {}
    };
    divs.push(newDiv);
    state.event.bracket = { brackets: divs };
    state.activeBracketId = newDiv.id;
    state.editingPos = null;
    persistDivisions(root);
  }

  function renameActiveDivision(root) {
    var state = getState(root);
    var active = getActiveBracket(state);
    if (!active) return;
    var name = prompt('Division name (e.g. Gold Bracket):', active.label || defaultLabelFor(0));
    if (name == null) return;
    name = name.trim().slice(0, 40);
    var divs = divisionList(state).map(function (d) {
      return d.id === active.id ? Object.assign({}, d, { label: name }) : d;
    });
    state.event.bracket = { brackets: divs };
    persistDivisions(root);
  }

  function removeActiveDivision(root) {
    var state = getState(root);
    var divs = divisionList(state);
    if (divs.length <= 1) { toast(root, 'A tournament needs at least one bracket.', 'err'); return; }
    var active = getActiveBracket(state);
    if (!active) return;
    if (!confirm('Remove "' + (active.label || 'this bracket') + '" and all its matches? This cannot be undone.')) return;
    var remaining = divs.filter(function (d) { return d.id !== active.id; });
    state.event.bracket = { brackets: remaining };
    state.activeBracketId = remaining[0].id;
    state.editingPos = null;
    persistDivisions(root);
  }

  function renderDivisionBar(root) {
    var state = getState(root);
    var bar = $(root, '[data-sk-le-division-bar]');
    if (!bar) return;
    var divisions = divisionList(state);
    if (!state.event || !state.event.slug || !divisions.length) {
      bar.setAttribute('hidden', '');
      bar.innerHTML = '';
      return;
    }
    bar.removeAttribute('hidden');
    var pills = divisions.map(function (d, i) {
      var active = d.id === state.activeBracketId;
      var name = d.label || 'Bracket';
      return ''
        + '<button type="button" class="sk-le-admin__division-pill" data-sk-le-division-pick="' + escapeHtml(d.id) + '"'
        +   ' data-active="' + (active ? 'true' : 'false') + '" style="--division-color:' + divColor(i) + ';">'
        +   '<span class="sk-le-admin__division-dot"></span>'
        +   escapeHtml(name)
        + '</button>';
    }).join('');
    var controls = '<button type="button" class="sk-le-admin__division-add" data-sk-le-division-add>＋ Division</button>'
      + '<button type="button" class="sk-le-admin__division-manage" data-sk-le-division-rename>Rename</button>'
      + (divisions.length > 1 ? '<button type="button" class="sk-le-admin__division-manage" data-sk-le-division-remove>Remove</button>' : '');
    bar.innerHTML = '<div class="sk-le-admin__division-pills">' + pills + '</div>'
                  + '<div class="sk-le-admin__division-controls">' + controls + '</div>';
    bindDivisionBar(root);
  }

  function bindDivisionBar(root) {
    var bar = $(root, '[data-sk-le-division-bar]');
    if (!bar) return;
    $$(bar, '[data-sk-le-division-pick]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var state = getState(root);
        state.activeBracketId = btn.getAttribute('data-sk-le-division-pick');
        state.editingPos = null;
        renderBracketPanel(root);
      });
    });
    var addBtn = $(bar, '[data-sk-le-division-add]');
    if (addBtn) addBtn.addEventListener('click', function () { addDivision(root); });
    var renameBtn = $(bar, '[data-sk-le-division-rename]');
    if (renameBtn) renameBtn.addEventListener('click', function () { renameActiveDivision(root); });
    var removeBtn = $(bar, '[data-sk-le-division-remove]');
    if (removeBtn) removeBtn.addEventListener('click', function () { removeActiveDivision(root); });
  }

  // ---------------------------------------------------------------------------
  // Tab bar
  // ---------------------------------------------------------------------------

  function bindTabs(root) {
    $$(root, '[data-sk-le-admin-tab]').forEach(function (tab) {
      tab.addEventListener('click', function () {
        setActiveTab(root, tab.getAttribute('data-sk-le-admin-tab'));
      });
    });
  }
  function setActiveTab(root, which) {
    $$(root, '[data-sk-le-admin-tab]').forEach(function (t) {
      t.setAttribute('aria-current', t.getAttribute('data-sk-le-admin-tab') === which ? 'true' : 'false');
    });
    $$(root, '[data-sk-le-admin-panel]').forEach(function (p) {
      p.setAttribute('data-active', p.getAttribute('data-sk-le-admin-panel') === which ? 'true' : 'false');
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  // ---------------------------------------------------------------------------
  // Bracket math + auto-advance
  // ---------------------------------------------------------------------------

  function buildRounds(size) {
    return (window.SkLiveEvent && window.SkLiveEvent.buildRounds)
      ? window.SkLiveEvent.buildRounds(size)
      : (function () {
          var rounds = [];
          var matchesInRound = size / 2;
          var posStart = 1;
          var idx = 1;
          while (matchesInRound >= 1) {
            var positions = [];
            for (var i = 0; i < matchesInRound; i++) positions.push(posStart + i);
            rounds.push({ roundIdx: idx, name: 'Round ' + idx, positions: positions });
            posStart += matchesInRound;
            matchesInRound = Math.floor(matchesInRound / 2);
            idx++;
          }
          return rounds;
        })();
  }

  /* Parent lookup: round-N match at index i (0-indexed within its round, 1..size/2 for round 1)
   * feeds into round-(N+1) match at index floor(i/2), into slot 'a' if i is even, 'b' if i is odd.
   * Returns { pos, slot } or null if this is the final. */
  function parentOf(pos, size) {
    var rounds = buildRounds(size);
    for (var r = 0; r < rounds.length - 1; r++) {
      var positions = rounds[r].positions;
      var idx = positions.indexOf(pos);
      if (idx === -1) continue;
      var parentRound = rounds[r + 1];
      var parentIdxInRound = Math.floor(idx / 2);
      return { pos: parentRound.positions[parentIdxInRound], slot: idx % 2 === 0 ? 'a' : 'b' };
    }
    return null;
  }

  /* After a match's winner is set/changed, propagate to parent IF the target slot
   * is empty or matches a previous auto-fill from this child. Tracks last-auto-fills
   * in bracket._meta.auto_filled_from so human edits aren't overwritten. */
  function autoAdvanceWinner(bracket, pos) {
    if (!bracket.auto_advance) return bracket;
    var size = Number(bracket.size);
    var matches = bracket.matches || [];
    var byPos = {};
    matches.forEach(function (m) { if (m && m.pos != null) byPos[m.pos] = m; });

    var child = byPos[pos];
    if (!child) return bracket;
    var winnerName = winnerOf(child);
    var parent = parentOf(pos, size);
    if (!parent) return bracket;
    var parentMatch = byPos[parent.pos];
    if (!parentMatch) {
      parentMatch = { pos: parent.pos, team_a: null, team_b: null, score_a: null, score_b: null };
      matches.push(parentMatch);
      byPos[parent.pos] = parentMatch;
    }

    var meta = bracket._meta || (bracket._meta = {});
    var filledFrom = meta.auto_filled_from || (meta.auto_filled_from = {});
    var key = parent.pos + ':' + parent.slot;     // e.g. "5:a"
    var slotField = parent.slot === 'a' ? 'team_a' : 'team_b';
    var current = parentMatch[slotField];
    var lastAutoFilledFrom = filledFrom[key];

    if (winnerName) {
      // Only overwrite if empty OR if the current value was previously auto-filled from this child
      if (!current || lastAutoFilledFrom === String(pos)) {
        parentMatch[slotField] = winnerName;
        filledFrom[key] = String(pos);
      }
    } else {
      // Winner cleared — clear parent slot if it was auto-filled from this child
      if (lastAutoFilledFrom === String(pos)) {
        parentMatch[slotField] = null;
        delete filledFrom[key];
      }
    }
    return bracket;
  }

  function winnerOf(match) {
    if (!match) return null;
    var aFilled = match.team_a && String(match.team_a).trim();
    var bFilled = match.team_b && String(match.team_b).trim();
    // Bye: only one slot filled, that team auto-advances
    if (aFilled && !bFilled) return match.team_a;
    if (bFilled && !aFilled) return match.team_b;
    // Both empty: no winner
    if (!aFilled && !bFilled) return null;
    // Both filled: need a score to determine winner
    var sa = match.score_a, sb = match.score_b;
    if (sa == null || sb == null) return null;
    if (sa > sb) return match.team_a;
    if (sb > sa) return match.team_b;
    return null;
  }

  // ---------------------------------------------------------------------------
  // Bracket render (uses viewer's renderMatchCell)
  // ---------------------------------------------------------------------------

  function renderBracketPanel(root) {
    var state = getState(root);
    var wrap = $(root, '[data-sk-le-admin-panel="bracket"] [data-sk-le-bracket-wrap]');
    if (!wrap) return;

    var event = state.event;
    if (event) ensureDivisions(state);
    var bracket = getActiveBracket(state);
    var size = bracket && Number(bracket.size);
    var teamCount = bracket && Number(bracket.team_count);
    var matches = (bracket && Array.isArray(bracket.matches)) ? bracket.matches : [];

    // Division switcher (self-hides when no event/slug/divisions)
    renderDivisionBar(root);

    // Update status line
    var eventLabel = $(root, '[data-sk-le-admin-bracket-event]');
    var metaLabel = $(root, '[data-sk-le-admin-bracket-meta]');
    var actions = $(root, '[data-sk-le-admin-bracket-actions]');
    if (eventLabel) eventLabel.textContent = event ? (event.title || '(unnamed)') : '—';
    if (metaLabel) {
      if (size) {
        var byeCount = teamCount ? Math.max(0, size - teamCount) : 0;
        var scoredCount = matches.filter(function (m) { return m && (m.score_a != null || m.score_b != null); }).length;
        var label = (teamCount || size) + ' teams · ' + scoredCount + '/' + (size - 1) + ' matches scored';
        if (byeCount > 0) label += ' · ' + byeCount + ' bye' + (byeCount === 1 ? '' : 's');
        metaLabel.textContent = label;
      } else {
        metaLabel.textContent = 'No bracket set up yet';
      }
    }
    if (actions) {
      if (size && event && event.slug) actions.removeAttribute('hidden');
      else actions.setAttribute('hidden', '');
    }

    if (!event) {
      wrap.innerHTML = '<div class="sk-le-admin__empty">Pick an event above to begin.</div>';
      return;
    }
    // Block bracket actions until event is saved server-side (has a slug)
    if (!event.slug) {
      wrap.innerHTML = ''
        + '<div class="sk-le-bracket-setup">'
        +   '<h3>Save event details first</h3>'
        +   '<p>Fill in the event title, slug, and date in <strong>Settings</strong>, then come back here to set up the bracket.</p>'
        +   '<button type="button" class="sk-le-admin__btn" data-sk-le-goto-settings>Go to Settings</button>'
        + '</div>';
      var goBtn = $(wrap, '[data-sk-le-goto-settings]');
      if (goBtn) goBtn.addEventListener('click', function () { setActiveTab(root, 'header'); });
      return;
    }
    if (!size || !matches.length) {
      wrap.innerHTML = renderSetupCard();
      bindSetupCard(root);
      return;
    }

    var rounds = buildRounds(size);
    var byPos = {};
    matches.forEach(function (m) { if (m && m.pos != null) byPos[m.pos] = m; });

    var maxCellsInRound = rounds.reduce(function (n, r) { return Math.max(n, r.positions.length); }, 1);
    var rowHeight = 168;
    var minHeight = maxCellsInRound * rowHeight;

    // Wrap in .sk-le__division-scroll so the admin tree gets the same
    // horizontal scroller + round-head top clearance the viewer uses.
    var html = '<div class="sk-le__division-scroll"><div class="sk-le__bracket" style="min-height:' + minHeight + 'px;">';
    rounds.forEach(function (rd, rIdx) {
      var count = rd.positions.length;
      html += '<div class="sk-le__bracket-round" data-round="' + rd.roundIdx + '" style="--rn-count:' + count + ';">';
      html += '<div class="sk-le__bracket-round-head">' + escapeHtml(rd.name) + '</div>';
      rd.positions.forEach(function (pos) {
        html += renderEditableCell(byPos[pos] || { pos: pos }, rIdx === rounds.length - 1, size);
      });
      html += '</div>';
    });
    html += '</div></div>';
    wrap.innerHTML = html;

    bindCellTaps(root);
    renderEditSheet(root);
  }

  function renderSetupCard() {
    var presets = [4, 6, 8, 12, 16, 24, 32];
    return ''
      + '<div class="sk-le-bracket-setup">'
      +   '<h3>Create the bracket</h3>'
      +   '<p>How many teams are in this tournament? Pick a preset or enter a custom number.</p>'
      +   '<div class="sk-le-bracket-setup__sizes">'
      +     presets.map(function (n) {
              var structure = Math.pow(2, Math.ceil(Math.log2(Math.max(2, n))));
              var label = (n === structure) ? Math.log2(n) + ' rounds' : (structure + '-bracket · ' + (structure - n) + ' byes');
              return ''
                + '<button type="button" class="sk-le-bracket-setup__size" data-sk-le-setup-size="' + n + '">'
                +   n
                +   '<span class="sk-le-bracket-setup__size-label">' + label + '</span>'
                + '</button>';
            }).join('')
      +   '</div>'
      +   '<div class="sk-le-bracket-setup__custom">'
      +     '<label>Or enter team count (2–64):</label>'
      +     '<div class="sk-le-bracket-setup__custom-row">'
      +       '<input type="number" min="2" max="64" placeholder="e.g. 10" data-sk-le-setup-custom-input>'
      +       '<button type="button" class="sk-le-admin__btn" data-sk-le-setup-custom>Create</button>'
      +     '</div>'
      +   '</div>'
      + '</div>';
  }

  function renderEditableCell(m, isFinal, _size) {
    var view = (window.SkLiveEvent && window.SkLiveEvent.renderMatchCell)
      ? window.SkLiveEvent.renderMatchCell(m, isFinal)
      : '';
    view = view.replace('<div class="sk-le-bm', '<div data-clickable="true" class="sk-le-bm');
    return '<div class="sk-le-bm-wrap" data-pos="' + m.pos + '">' + view + '</div>';
  }

  /* Edit form lives in a fixed bottom sheet (mobile) / centered modal (desktop)
   * so its positioning isn't tied to the cell's row geometry. */
  function renderEditSheet(root) {
    var state = getState(root);
    var sheet = $(root, '[data-sk-le-edit-sheet]');
    if (!sheet) return;
    var pos = state.editingPos;
    if (!pos) {
      sheet.setAttribute('data-open', 'false');
      sheet.innerHTML = '';
      return;
    }
    var bracket = getActiveBracket(state);
    var size = bracket && Number(bracket.size);
    var matches = (bracket && bracket.matches) || [];
    var m = matches.find(function (x) { return x && x.pos === pos; }) || { pos: pos };
    var parent = parentOf(pos, size);
    var roundIdx = roundIndexOf(pos, size);
    var roundName = roundNameOf(roundIdx, size);

    sheet.setAttribute('data-open', 'true');
    sheet.innerHTML = ''
      + '<div class="sk-le-edit-sheet__backdrop" data-sk-le-edit-cancel></div>'
      + '<div class="sk-le-edit-sheet__panel" role="dialog" aria-label="Edit match">'
      +   '<div class="sk-le-edit-sheet__head">'
      +     '<div class="sk-le-edit-sheet__eyebrow">' + escapeHtml(roundName) + ' · Match ' + pos + '</div>'
      +     '<button type="button" class="sk-le-edit-sheet__close" data-sk-le-edit-cancel aria-label="Close">×</button>'
      +   '</div>'
      +   '<div class="sk-le-edit-sheet__body" data-sk-le-edit-form data-pos="' + pos + '">'
      +     '<div class="sk-le-edit-sheet__row">'
      +       '<div class="sk-le-edit-sheet__field"><label>Team A</label><input data-field="team_a" value="' + escapeHtml(m.team_a || '') + '" maxlength="60" autofocus></div>'
      +       '<div class="sk-le-edit-sheet__field sk-le-edit-sheet__field--score"><label>Score</label><input data-field="score_a" type="number" inputmode="numeric" min="0" max="99" value="' + escapeHtml(m.score_a == null ? '' : m.score_a) + '"></div>'
      +     '</div>'
      +     '<div class="sk-le-edit-sheet__row">'
      +       '<div class="sk-le-edit-sheet__field"><label>Team B</label><input data-field="team_b" value="' + escapeHtml(m.team_b || '') + '" maxlength="60"></div>'
      +       '<div class="sk-le-edit-sheet__field sk-le-edit-sheet__field--score"><label>Score</label><input data-field="score_b" type="number" inputmode="numeric" min="0" max="99" value="' + escapeHtml(m.score_b == null ? '' : m.score_b) + '"></div>'
      +     '</div>'
      +     '<div class="sk-le-edit-sheet__row">'
      +       '<div class="sk-le-edit-sheet__field"><label>Court (optional)</label><input data-field="court" value="' + escapeHtml(m.court || '') + '" maxlength="32" placeholder="Court 1"></div>'
      +       '<div class="sk-le-edit-sheet__field"><label>Time (optional)</label><input data-field="time" value="' + escapeHtml(m.time || '') + '" maxlength="32" placeholder="9:00am"></div>'
      +     '</div>'
      +     (parent
          ? '<div class="sk-le-edit-sheet__hint">Winner advances to <strong>Match ' + parent.pos + ' · Slot ' + parent.slot.toUpperCase() + '</strong></div>'
          : '<div class="sk-le-edit-sheet__hint">This is the final. Winner is the champion.</div>')
      +     '<div class="sk-le-edit-sheet__actions">'
      +       '<button type="button" class="sk-le-edit-sheet__btn" data-sk-le-edit-cancel>Cancel</button>'
      +       '<button type="button" class="sk-le-edit-sheet__btn sk-le-edit-sheet__btn--primary" data-sk-le-edit-save>Save</button>'
      +     '</div>'
      +   '</div>'
      + '</div>';

    bindEditForm(root);
  }

  function roundIndexOf(pos, size) {
    if (!size) return null;
    var rounds = buildRounds(size);
    for (var i = 0; i < rounds.length; i++) {
      if (rounds[i].positions.indexOf(pos) !== -1) return i;
    }
    return null;
  }
  function roundNameOf(roundIdx, size) {
    if (roundIdx == null || !size) return 'Match';
    var rounds = buildRounds(size);
    return (rounds[roundIdx] && rounds[roundIdx].name) || 'Match';
  }

  function bindSetupCard(root) {
    $$(root, '[data-sk-le-setup-size]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var count = parseInt(btn.getAttribute('data-sk-le-setup-size'), 10);
        changeTeamCount(root, count);
      });
    });
    var customBtn = $(root, '[data-sk-le-setup-custom]');
    var customInput = $(root, '[data-sk-le-setup-custom-input]');
    if (customBtn && customInput) {
      customBtn.addEventListener('click', function () {
        var n = parseInt(customInput.value, 10);
        if (!n || n < 2 || n > 64) { toast(root, 'Team count must be between 2 and 64.', 'err'); return; }
        changeTeamCount(root, n);
      });
      customInput.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') { e.preventDefault(); customBtn.click(); }
      });
    }
  }

  function nextPow2(n) {
    if (!n || n < 2) return 2;
    return Math.pow(2, Math.ceil(Math.log2(n)));
  }

  /* Non-destructive resize. Any team count 2-64. Bracket structure auto-rounds
   * to next power of 2; extra slots become byes. Going UP preserves all existing
   * matches by shifting their pos forward and adding empty round-1 slots. Going
   * DOWN drops the dropped-round count from the front (requires confirm). */
  function changeTeamCount(root, newCount) {
    var state = getState(root);
    if (!state.event || !state.event.slug) {
      toast(root, 'Save event details first.', 'err');
      return;
    }
    if (!newCount || newCount < 2 || newCount > 64) {
      toast(root, 'Team count must be between 2 and 64.', 'err');
      return;
    }
    var current = getActiveBracket(state) || {};
    var oldSize = Number(current.size) || 0;
    var newSize = nextPow2(newCount);
    var oldMatches = Array.isArray(current.matches) ? current.matches : [];

    // First-time init (empty bracket)
    if (!oldSize) {
      var matches = [];
      for (var p = 1; p <= newSize - 1; p++) {
        matches.push({ pos: p, team_a: null, team_b: null, score_a: null, score_b: null });
      }
      saveBracket(root, { size: newSize, team_count: newCount, auto_advance: true, matches: matches });
      return;
    }

    // Same structure — just update team_count
    if (newSize === oldSize) {
      var same = Object.assign({}, current, { team_count: newCount });
      saveBracket(root, same);
      return;
    }

    // Grow: shift all positions forward, prepend empty round-1 matches
    if (newSize > oldSize) {
      var shift = newSize - oldSize;
      var shifted = oldMatches.map(function (m) {
        return Object.assign({}, m, { pos: m.pos + shift });
      });
      for (var q = 1; q <= shift; q++) {
        shifted.push({ pos: q, team_a: null, team_b: null, score_a: null, score_b: null });
      }
      shifted.sort(function (a, b) { return a.pos - b.pos; });
      var newMeta = remapMetaShift(current._meta, shift);
      saveBracket(root, {
        size: newSize,
        team_count: newCount,
        auto_advance: current.auto_advance !== false,
        matches: shifted,
        _meta: newMeta
      });
      toast(root, 'Bracket expanded to ' + newSize + ' teams. Existing matches preserved.', 'ok');
      return;
    }

    // Shrink: drop the earliest (oldSize - newSize) match positions.
    // Lossy — needs explicit confirm with a count of teams that would be dropped.
    var dropCount = oldSize - newSize;
    var droppedTeams = [];
    oldMatches.forEach(function (m) {
      if (m.pos <= dropCount) {
        if (m.team_a) droppedTeams.push(m.team_a);
        if (m.team_b) droppedTeams.push(m.team_b);
      }
    });
    var msg = 'Shrink to ' + newSize + '-team bracket?\n\nThis drops the first ' + dropCount + ' match slot' + (dropCount === 1 ? '' : 's')
      + (droppedTeams.length ? ' (containing ' + droppedTeams.length + ' team' + (droppedTeams.length === 1 ? '' : 's') + ': ' + droppedTeams.slice(0, 4).join(', ') + (droppedTeams.length > 4 ? '…' : '') + ')' : '')
      + '.';
    if (!confirm(msg)) return;
    var kept = oldMatches
      .filter(function (m) { return m.pos > dropCount; })
      .map(function (m) { return Object.assign({}, m, { pos: m.pos - dropCount }); });
    var shrunkMeta = remapMetaShift(current._meta, -dropCount);
    saveBracket(root, {
      size: newSize,
      team_count: newCount,
      auto_advance: current.auto_advance !== false,
      matches: kept,
      _meta: shrunkMeta
    });
    toast(root, 'Bracket shrunk to ' + newSize + ' teams.', 'ok');
  }

  function remapMetaShift(meta, shift) {
    if (!meta || !meta.auto_filled_from) return meta || {};
    var out = {};
    Object.keys(meta.auto_filled_from).forEach(function (key) {
      var parts = key.split(':');
      var parentPos = parseInt(parts[0], 10) + shift;
      var slot = parts[1];
      var srcPos = parseInt(meta.auto_filled_from[key], 10) + shift;
      if (parentPos > 0 && srcPos > 0) {
        out[parentPos + ':' + slot] = String(srcPos);
      }
    });
    return Object.assign({}, meta, { auto_filled_from: out });
  }

  function openResizePicker(root) {
    var state = getState(root);
    var bracket = getActiveBracket(state);
    var current = (bracket && Number(bracket.team_count)) || (bracket && Number(bracket.size)) || 0;
    var input = prompt('How many teams? (2–64)\nGrowing preserves existing matches. Shrinking will warn before dropping rounds.\n(Current: ' + (current || 'none') + ')', String(current || 8));
    if (input == null) return;
    var n = parseInt(input.trim(), 10);
    if (!n) { toast(root, 'Enter a number between 2 and 64.', 'err'); return; }
    changeTeamCount(root, n);
  }

  function bindCellTaps(root) {
    var wrap = $(root, '[data-sk-le-admin-panel="bracket"] [data-sk-le-bracket-wrap]');
    if (!wrap) return;
    wrap.addEventListener('click', function (e) {
      var cell = e.target.closest('.sk-le-bm');
      if (!cell) return;
      var wrapEl = cell.closest('.sk-le-bm-wrap');
      if (!wrapEl) return;
      var pos = parseInt(wrapEl.getAttribute('data-pos'), 10);
      if (!pos) return;
      var state = getState(root);
      state.editingPos = pos;
      renderEditSheet(root);
    });
  }

  function bindEditForm(root) {
    var sheet = $(root, '[data-sk-le-edit-sheet]');
    if (!sheet) return;
    $$(sheet, '[data-sk-le-edit-cancel]').forEach(function (el) {
      el.addEventListener('click', function () {
        getState(root).editingPos = null;
        renderEditSheet(root);
      });
    });
    var saveBtn = $(sheet, '[data-sk-le-edit-save]');
    if (!saveBtn) return;
    saveBtn.addEventListener('click', function () {
      var form = $(sheet, '[data-sk-le-edit-form]');
      var pos = parseInt(form.getAttribute('data-pos'), 10);
      var fields = {};
      $$(form, '[data-field]').forEach(function (input) {
        var f = input.getAttribute('data-field');
        var v = input.value.trim();
        if (f === 'score_a' || f === 'score_b') fields[f] = v === '' ? null : Math.max(0, Math.min(99, parseInt(v, 10) || 0));
        else fields[f] = v || null;
      });
      var state = getState(root);
      var active = getActiveBracket(state) || {};
      var bracket = JSON.parse(JSON.stringify(active));
      bracket.size = Number(bracket.size) || Number(active.size);
      bracket.auto_advance = bracket.auto_advance !== false;
      bracket.matches = bracket.matches || [];
      var byPos = {};
      bracket.matches.forEach(function (m) { byPos[m.pos] = m; });
      var match = byPos[pos];
      if (!match) {
        match = { pos: pos };
        bracket.matches.push(match);
        byPos[pos] = match;
      }
      Object.keys(fields).forEach(function (k) { match[k] = fields[k]; });

      autoAdvanceWinner(bracket, pos);

      state.editingPos = null;
      saveBracket(root, bracket);
    });
  }

  // ---------------------------------------------------------------------------
  // Save bracket (single upsert with optimistic UI)
  // ---------------------------------------------------------------------------

  /* Merge an updated single-division object back into the active division,
   * reconstruct the whole { brackets: [...] } JSONB, and persist it. The
   * edge function patches `bracket` as one opaque blob, so every save ships
   * all divisions from local state — safe because one director edits at a
   * time and local state holds every division. */
  function saveBracket(root, updatedDivision) {
    var state = getState(root);
    if (!state.event) return;
    var prev = state.event.bracket;
    var divs = divisionList(state);
    var active = getActiveBracket(state);
    var activeId = active ? active.id : (state.activeBracketId || defaultIdFor(0));
    var activeLabel = active ? active.label : '';
    var merged = Object.assign({}, updatedDivision, {
      id: activeId,
      label: (updatedDivision && updatedDivision.label != null) ? updatedDivision.label : activeLabel
    });
    var replaced = false;
    var newDivs = divs.map(function (d) {
      if (d.id === activeId) { replaced = true; return merged; }
      return d;
    });
    if (!replaced) { newDivs.push(merged); state.activeBracketId = merged.id; }

    state.event.bracket = { brackets: newDivs };
    renderBracketPanel(root);
    jsonPost(PROXY + '/event/upsert', { slug: state.event.slug, bracket: state.event.bracket })
      .then(function () { toast(root, 'Saved.', 'ok'); return loadSnapshot(root, state.event.slug); })
      .catch(function (err) {
        state.event.bracket = prev;
        renderBracketPanel(root);
        toast(root, 'Save failed: ' + (err.detail && err.detail.error || err.message), 'err');
      });
  }

  /* Reset (deprecated path — kept for Settings tab if still wired). Same logic as openResizePicker. */
  function resetBracketSize(root) { openResizePicker(root); }

  /* Create a fresh event: clears state and switches to Header tab with blank form.
   * Saving the form creates the new event server-side. */
  function newEvent(root) {
    var state = getState(root);
    state.event = {
      id: null,
      slug: '',
      title: '',
      status: 'draft',
      starts_at: null,
      ends_at: null,
      header: {},
      bracket: {}
    };
    state.matches = [];
    state.announcements = [];
    state.editingPos = null;
    var form = $(root, '[data-sk-le-event-form]');
    if (form) form.reset();
    setActiveTab(root, 'header');
    renderBracketPanel(root);
    renderAnnouncementsList(root);
    fillHeaderForm(root);
    toast(root, 'Fill in the event details, then Save to create.', 'ok');
  }

  // ---------------------------------------------------------------------------
  // Announce panel
  // ---------------------------------------------------------------------------

  function renderAnnouncementsList(root) {
    var state = getState(root);
    var list = $(root, '[data-sk-le-admin-announcement-list]');
    if (!list) return;
    var items = state.announcements || [];
    if (!items.length) {
      list.innerHTML = '<li class="sk-le-admin__empty">No announcements yet.</li>';
      return;
    }
    var sorted = items.slice().sort(function (a, b) {
      if (a.pinned !== b.pinned) return a.pinned ? -1 : 1;
      return (b.created_at || '').localeCompare(a.created_at || '');
    });
    list.innerHTML = sorted.map(function (a) {
      var pinned = a.pinned ? ' sk-le-admin-ann--pinned' : '';
      return ''
        + '<li class="sk-le-admin-ann' + pinned + '" data-ann-id="' + escapeHtml(a.id) + '">'
        +   '<div class="sk-le-admin-ann__body">' + escapeHtml(a.body) + '</div>'
        +   '<div class="sk-le-admin-ann__meta">'
        +     '<span>' + escapeHtml(a.created_at || '') + '</span>'
        +     '<button type="button" data-action="delete-announcement">Delete</button>'
        +   '</div>'
        + '</li>';
    }).join('');
  }

  function bindAnnounceTab(root) {
    var form = $(root, '[data-sk-le-announcement-form]');
    if (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        var state = getState(root);
        if (!state.event) { toast(root, 'Select an event first.', 'err'); return; }
        var data = new FormData(form);
        var body = { event_slug: state.event.slug, body: data.get('body'), pinned: data.get('pinned') === 'on' };
        jsonPost(PROXY + '/announcement/create', body)
          .then(function () { form.reset(); toast(root, 'Posted.', 'ok'); return loadSnapshot(root, state.event.slug); })
          .catch(function (err) { toast(root, 'Post failed: ' + (err.detail && err.detail.error || err.message), 'err'); });
      });
    }
    var list = $(root, '[data-sk-le-admin-announcement-list]');
    if (list) {
      list.addEventListener('click', function (e) {
        var btn = e.target.closest('[data-action="delete-announcement"]');
        if (!btn) return;
        var li = btn.closest('[data-ann-id]');
        if (!li) return;
        if (!confirm('Delete this announcement?')) return;
        var id = li.getAttribute('data-ann-id');
        jsonPost(PROXY + '/announcement/delete/' + encodeURIComponent(id), {})
          .then(function () { var state = getState(root); return loadSnapshot(root, state.event.slug); })
          .catch(function (err) { toast(root, 'Delete failed: ' + (err.detail && err.detail.error || err.message), 'err'); });
      });
    }
  }

  // ---------------------------------------------------------------------------
  // Header panel
  // ---------------------------------------------------------------------------

  function fillHeaderForm(root) {
    var state = getState(root);
    var form = $(root, '[data-sk-le-event-form]');
    if (!form || !state.event) return;
    var e = state.event;
    var h = e.header || {};
    form.elements['title'].value = e.title || '';
    form.elements['slug'].value = e.slug || '';
    form.elements['status'].value = e.status || 'draft';
    form.elements['starts_at'].value = (e.starts_at || '').slice(0, 16);
    form.elements['ends_at'].value = (e.ends_at || '').slice(0, 16);
    if (form.elements['sport']) form.elements['sport'].value = h.sport || '';
    form.elements['location'].value = h.location || '';
    var dirText = '';
    if (h.director) {
      if (typeof h.director === 'string') dirText = h.director;
      else if (h.director.name) {
        dirText = h.director.email ? h.director.name + ' (' + h.director.email + ')' : h.director.name;
      }
    }
    form.elements['director'].value = dirText;
    form.elements['banner_image_url'].value = h.banner_image_url || '';
  }

  // ---------------------------------------------------------------------------
  // Admin allowlist management (owner-only). Owner-detection is implicit:
  // GET /admins returns 403 for non-owners, which hides the form and shows a
  // "no access" message instead.
  // ---------------------------------------------------------------------------

  function loadAdmins(root) {
    var listEl = $(root, '[data-sk-le-admins-list]');
    var emptyEl = $(root, '[data-sk-le-admins-empty]');
    var formEl = $(root, '[data-sk-le-admin-form]');
    var noAccessEl = $(root, '[data-sk-le-admins-noaccess]');
    var archivedToggle = $(root, '[data-sk-le-admin-archived-toggle]');
    if (!listEl) return;
    if (emptyEl) { emptyEl.textContent = 'Loading admins…'; emptyEl.removeAttribute('hidden'); }
    if (formEl) formEl.setAttribute('hidden', '');
    if (noAccessEl) noAccessEl.setAttribute('hidden', '');

    jsonGet(PROXY + '/admins')
      .then(function (data) {
        var admins = (data && data.admins) || [];
        renderAdminsList(root, admins);
        if (emptyEl) emptyEl.setAttribute('hidden', '');
        if (formEl) formEl.removeAttribute('hidden');
        // Successful admins fetch == owner role. Surface the "Show archived"
        // top-bar toggle, which is also owner-gated server-side.
        if (archivedToggle) archivedToggle.removeAttribute('hidden');
      })
      .catch(function (err) {
        listEl.innerHTML = '';
        if (emptyEl) emptyEl.setAttribute('hidden', '');
        if (formEl) formEl.setAttribute('hidden', '');
        if (archivedToggle) archivedToggle.setAttribute('hidden', '');
        if (err.detail && /Owner role required/i.test(err.detail.error || '')) {
          if (noAccessEl) noAccessEl.removeAttribute('hidden');
        } else if (emptyEl) {
          emptyEl.textContent = 'Could not load admins: ' + (err.detail && err.detail.error || err.message);
          emptyEl.removeAttribute('hidden');
        }
      });
  }

  function renderAdminsList(root, admins) {
    var list = $(root, '[data-sk-le-admins-list]');
    if (!list) return;
    if (!admins.length) {
      list.innerHTML = '<li class="sk-le-admin__empty">No admins yet.</li>';
      return;
    }
    list.innerHTML = admins.map(function (a) {
      var roleBadge = '<span class="sk-le-admin__role-badge" data-role="' + escapeHtml(a.role) + '">' + escapeHtml(a.role) + '</span>';
      return ''
        + '<li class="sk-le-admin__admin-row" data-email="' + escapeHtml(a.email) + '">'
        +   '<div class="sk-le-admin__admin-info">'
        +     '<div class="sk-le-admin__admin-email">' + escapeHtml(a.email) + '</div>'
        +     '<div class="sk-le-admin__admin-meta">' + roleBadge + '</div>'
        +   '</div>'
        +   '<button type="button" class="sk-le-admin__chip" data-action="remove-admin">Remove</button>'
        + '</li>';
    }).join('');
  }

  function bindAdminsSection(root) {
    var form = $(root, '[data-sk-le-admin-form]');
    if (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        var data = new FormData(form);
        var body = {
          email: (data.get('email') || '').toString().trim(),
          role: data.get('role') || 'editor'
        };
        if (!body.email) { toast(root, 'Email is required.', 'err'); return; }
        jsonPost(PROXY + '/admins/upsert', body)
          .then(function () { form.reset(); toast(root, 'Admin saved.', 'ok'); loadAdmins(root); })
          .catch(function (err) { toast(root, 'Save failed: ' + (err.detail && err.detail.error || err.message), 'err'); });
      });
    }
    var list = $(root, '[data-sk-le-admins-list]');
    if (list) {
      list.addEventListener('click', function (e) {
        var btn = e.target.closest('[data-action="remove-admin"]');
        if (!btn) return;
        var row = btn.closest('[data-email]');
        if (!row) return;
        var email = row.getAttribute('data-email');
        if (!confirm('Remove ' + email + ' from admin access?')) return;
        jsonPost(PROXY + '/admins/delete/' + encodeURIComponent(email), {})
          .then(function () { toast(root, 'Admin removed.', 'ok'); loadAdmins(root); })
          .catch(function (err) { toast(root, 'Remove failed: ' + (err.detail && err.detail.error || err.message), 'err'); });
      });
    }
  }

  // ---------------------------------------------------------------------------
  // Danger zone: archive / unarchive / delete the loaded event
  // ---------------------------------------------------------------------------

  function renderDangerZone(root) {
    var state = getState(root);
    var zone = $(root, '[data-sk-le-danger-zone]');
    if (!zone) return;
    var event = state.event;
    // Hide the zone entirely when no event is loaded OR when the event hasn't
    // been saved yet (no slug) — there's nothing to archive/delete.
    if (!event || !event.slug) {
      zone.setAttribute('hidden', '');
      return;
    }
    zone.removeAttribute('hidden');
    var statusEl = $(root, '[data-sk-le-danger-status]');
    var archived = event.status === 'archived';
    if (statusEl) {
      statusEl.innerHTML = 'This event is currently <strong>' + escapeHtml(event.status || 'draft') + '</strong>. '
        + (archived
            ? 'Unarchive to make it visible on the storefront again, or delete it permanently.'
            : 'Archive hides this event from the storefront but keeps the data. Delete is permanent — there\'s no undo.');
    }
    var archBtn = $(root, '[data-sk-le-event-archive]');
    var unarchBtn = $(root, '[data-sk-le-event-unarchive]');
    if (archBtn)   { if (archived) archBtn.setAttribute('hidden', '');   else archBtn.removeAttribute('hidden'); }
    if (unarchBtn) { if (archived) unarchBtn.removeAttribute('hidden'); else unarchBtn.setAttribute('hidden', ''); }
  }

  function bindDangerZone(root) {
    var archBtn = $(root, '[data-sk-le-event-archive]');
    if (archBtn) archBtn.addEventListener('click', function () { archiveCurrentEvent(root); });
    var unarchBtn = $(root, '[data-sk-le-event-unarchive]');
    if (unarchBtn) unarchBtn.addEventListener('click', function () { unarchiveCurrentEvent(root); });
    var delBtn = $(root, '[data-sk-le-event-delete]');
    if (delBtn) delBtn.addEventListener('click', function () { deleteCurrentEvent(root); });
  }

  function archiveCurrentEvent(root) {
    var state = getState(root);
    if (!state.event || !state.event.slug) return;
    var slug = state.event.slug;
    if (!confirm('Archive "' + (state.event.title || slug) + '"?\n\nIt will be hidden from the storefront and the event picker. You can unarchive it later by enabling the Archived toggle in the top bar.')) return;
    jsonPost(PROXY + '/event/archive/' + encodeURIComponent(slug), {})
      .then(function () {
        toast(root, 'Event archived.', 'ok');
        // After archiving, the event drops out of the default picker; load
        // the list afresh and pick whatever's first.
        return loadEventList(root);
      })
      .catch(function (err) { toast(root, 'Archive failed: ' + (err.detail && err.detail.error || err.message), 'err'); });
  }

  function unarchiveCurrentEvent(root) {
    var state = getState(root);
    if (!state.event || !state.event.slug) return;
    // Unarchive == upsert with a non-archived status. Default to "draft" so
    // it doesn't accidentally appear as live before the admin reviews it.
    var body = {
      slug: state.event.slug,
      title: state.event.title,
      status: 'draft'
    };
    jsonPost(PROXY + '/event/upsert', body)
      .then(function () { toast(root, 'Event unarchived (status: draft).', 'ok'); return loadEventList(root, state.event.slug); })
      .catch(function (err) { toast(root, 'Unarchive failed: ' + (err.detail && err.detail.error || err.message), 'err'); });
  }

  function deleteCurrentEvent(root) {
    var state = getState(root);
    if (!state.event || !state.event.slug) return;
    var slug = state.event.slug;
    var typed = prompt(
      'PERMANENT DELETE\n\nThis will erase the event "' + (state.event.title || slug) + '" and ALL its data (bracket, matches, announcements). This cannot be undone.\n\nType the slug to confirm:\n' + slug
    );
    if (typed == null) return;
    if (typed.trim() !== slug) { toast(root, 'Slug did not match. Delete cancelled.', 'err'); return; }
    jsonPost(PROXY + '/event/delete/' + encodeURIComponent(slug), {})
      .then(function () {
        toast(root, 'Event permanently deleted.', 'ok');
        var s = getState(root); s.event = null; s.matches = []; s.announcements = [];
        return loadEventList(root);
      })
      .catch(function (err) { toast(root, 'Delete failed: ' + (err.detail && err.detail.error || err.message), 'err'); });
  }

  function bindHeaderTab(root) {
    var form = $(root, '[data-sk-le-event-form]');
    if (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        var state = getState(root);
        var existingH = (state.event && state.event.header) || {};
        var data = new FormData(form);
        var dirRaw = (data.get('director') || '').trim();
        var dirOut = '';
        if (dirRaw) {
          var m = dirRaw.match(/^(.+?)\s*\(([^)]+@[^)]+)\)\s*$/);
          dirOut = m ? { name: m[1].trim(), email: m[2].trim() } : dirRaw;
        }
        var sportRaw = (data.get('sport') || '').trim().slice(0, 40);
        var header = Object.assign({}, existingH, {
          sport: sportRaw,
          location: data.get('location') || '',
          director: dirOut,
          banner_image_url: data.get('banner_image_url') || ''
        });
        var body = {
          slug: data.get('slug'),
          title: data.get('title'),
          status: data.get('status') || 'draft',
          starts_at: data.get('starts_at') || null,
          ends_at: data.get('ends_at') || null,
          header: header
        };
        jsonPost(PROXY + '/event/upsert', body)
          .then(function () { toast(root, 'Event saved.', 'ok'); return loadEventList(root, body.slug); })
          .catch(function (err) { toast(root, 'Save failed: ' + (err.detail && err.detail.error || err.message), 'err'); });
      });
    }
    var reset = $(root, '[data-sk-le-bracket-reset]');
    if (reset) reset.addEventListener('click', function () { resetBracketSize(root); });
    var resize = $(root, '[data-sk-le-admin-resize]');
    if (resize) resize.addEventListener('click', function () { openResizePicker(root); });
    var newBtn = $(root, '[data-sk-le-admin-new-event]');
    if (newBtn) newBtn.addEventListener('click', function () { newEvent(root); });
  }

  // ---------------------------------------------------------------------------
  // Event list + snapshot loader
  // ---------------------------------------------------------------------------

  function loadEventList(root, preferredSlug) {
    var includeArchived = isArchivedToggleOn(root);
    var url = PROXY + '/list' + (includeArchived ? '?include_archived=1' : '');
    return jsonGet(url).then(function (data) {
      var events = (data && data.events) || [];
      var select = $(root, '[data-sk-le-admin-event-select]');
      if (select) {
        select.innerHTML = events.length
          ? events.map(function (e) {
              var sel = (preferredSlug && e.slug === preferredSlug) ? ' selected' : '';
              var label = escapeHtml(e.title) + ' (' + escapeHtml(e.status) + ')';
              return '<option value="' + escapeHtml(e.slug) + '"' + sel + '>' + label + '</option>';
            }).join('')
          : '<option value="">No events</option>';
      }
      var current = preferredSlug || (events[0] && events[0].slug);
      if (!current) {
        // No events available — clear the loaded event state so the bracket
        // panel + danger zone render their empty/locked states.
        var state = getState(root);
        state.event = null;
        renderBracketPanel(root);
        renderDangerZone(root);
        return;
      }
      return loadSnapshot(root, current);
    });
  }

  function isArchivedToggleOn(root) {
    var cb = $(root, '[data-sk-le-admin-archived-checkbox]');
    return !!(cb && cb.checked);
  }

  function bindArchivedToggle(root) {
    // The toggle is owner-only (server-side enforced); show it whenever the
    // admins endpoint succeeds (which itself requires owner). Probe via the
    // admins-section visibility set by loadAdmins.
    var label = $(root, '[data-sk-le-admin-archived-toggle]');
    var cb = $(root, '[data-sk-le-admin-archived-checkbox]');
    if (!label || !cb) return;
    cb.addEventListener('change', function () {
      // Reload the event list with/without archived. Keep the current selection
      // if possible.
      var state = getState(root);
      var current = state.event && state.event.slug;
      loadEventList(root, current);
    });
  }

  function loadSnapshot(root, slug) {
    return jsonGet(PROXY + '/read/' + encodeURIComponent(slug))
      .then(function (snap) {
        if (!snap || !snap.event) return;
        var state = getState(root);
        state.event = snap.event;
        state.matches = snap.matches || [];
        state.announcements = snap.announcements || [];
        state.editingPos = null;
        renderBracketPanel(root);
        renderAnnouncementsList(root);
        fillHeaderForm(root);
        renderDangerZone(root);
      })
      .catch(function (err) {
        // 404 most commonly means the event was archived and the picker has
        // include_archived=0; the /read endpoint refuses archived events to
        // anon. Fetch the row directly via the list endpoint with archived
        // included (owner-only) so the admin can still load + unarchive it.
        if (err && err.detail && /not found/i.test(err.detail.error || '')) {
          return jsonGet(PROXY + '/list?include_archived=1').then(function (data) {
            var events = (data && data.events) || [];
            var ev = events.find(function (e) { return e.slug === slug; });
            if (!ev) throw err;
            // Fabricate a minimal event row from the list payload so the form
            // loads. Bracket / matches / announcements stay empty until the
            // user unarchives + re-fetches.
            var state = getState(root);
            state.event = { id: ev.id, slug: ev.slug, title: ev.title, status: ev.status, starts_at: ev.starts_at, ends_at: ev.ends_at, header: {}, bracket: {} };
            state.matches = [];
            state.announcements = [];
            state.editingPos = null;
            renderBracketPanel(root);
            renderAnnouncementsList(root);
            fillHeaderForm(root);
            renderDangerZone(root);
            toast(root, 'Archived event loaded. Save with a non-archived status to restore it.', 'ok');
          });
        }
        throw err;
      });
  }

  function bindEventSelect(root) {
    var select = $(root, '[data-sk-le-admin-event-select]');
    if (!select) return;
    select.addEventListener('change', function () {
      var slug = select.value;
      if (!slug) return;
      loadSnapshot(root, slug);
    });
  }

  // ---------------------------------------------------------------------------
  // Boot
  // ---------------------------------------------------------------------------

  function boot() {
    var roots = $$(document, '[data-sk-le-admin-root]');
    if (roots.length === 0) return;
    roots.forEach(function (root) {
      bindTabs(root);
      bindAnnounceTab(root);
      bindHeaderTab(root);
      bindAdminsSection(root);
      bindDangerZone(root);
      bindArchivedToggle(root);
      bindEventSelect(root);
      loadEventList(root).catch(function (err) {
        toast(root, 'Could not load events: ' + (err.message || err), 'err');
      });
      // Load admin list once on boot; the Settings tab will display it.
      loadAdmins(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
}());
